import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import config from '../config';

interface Chatbot {
    id: number;
    lead_id: number;
    lead_name: string;
    website_id: number;
    website_title: string;
    platform: string;
    ai_provider: string;
    client_id: string;
    embed_code: string;
    notes: string;
    created_at: string;
    updated_at: string;
}

interface Lead {
    id: number;
    name: string;
}

interface Website {
    id: number;
    website_title: string;
}

const Chatbots: React.FC = () => {
    const [chatbots, setChatbots] = useState<Chatbot[]>([]);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [websites, setWebsites] = useState<Website[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingChatbot, setEditingChatbot] = useState<Chatbot | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        lead_id: '',
        website_id: '',
        platform: '',
        ai_provider: '',
        client_id: '',
        embed_code: '',
        notes: ''
    });

    // Add a style object for truncating long dropdown option text
    const selectStyles = {
        maxWidth: '100%',
        textOverflow: 'ellipsis'
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                await Promise.all([
                    fetchChatbots(),
                    fetchLeads(),
                    fetchWebsites()
                ]);
            } catch (err) {
                setError('Failed to load data. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const fetchChatbots = async () => {
        try {
            const response = await fetch(`${config.apiBaseUrl}/chatbots/read.php`);
            if (!response.ok) throw new Error('Failed to fetch chatbots');
            const data = await response.json();
            setChatbots(data.records || []);
        } catch (error) {
            console.error('Error fetching chatbots:', error);
            throw error;
        }
    };

    const fetchLeads = async () => {
        try {
            const response = await fetch(`${config.apiBaseUrl}/leads/read_basic.php`);
            if (!response.ok) throw new Error('Failed to fetch leads');
            const data = await response.json();
            setLeads(data.records || []);
        } catch (error) {
            console.error('Error fetching leads:', error);
            throw error;
        }
    };

    const fetchWebsites = async () => {
        try {
            const response = await fetch(`${config.apiBaseUrl}/client_websites/read.php`);
            if (!response.ok) throw new Error('Failed to fetch websites');
            const data = await response.json();
            setWebsites(data.records || []);
        } catch (error) {
            console.error('Error fetching websites:', error);
            throw error;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const url = editingChatbot
            ? `${config.apiBaseUrl}/chatbots/update.php`
            : `${config.apiBaseUrl}/chatbots/create.php`;
        const method = editingChatbot ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editingChatbot ? { ...formData, id: editingChatbot.id } : formData),
            });

            if (response.ok) {
                setIsModalOpen(false);
                setEditingChatbot(null);
                setFormData({
                    lead_id: '',
                    website_id: '',
                    platform: '',
                    ai_provider: '',
                    client_id: '',
                    embed_code: '',
                    notes: ''
                });
                fetchChatbots();
            }
        } catch (error) {
            console.error('Error saving chatbot:', error);
        }
    };

    const handleEdit = (chatbot: Chatbot) => {
        setEditingChatbot(chatbot);
        setFormData({
            lead_id: chatbot.lead_id.toString(),
            website_id: chatbot.website_id.toString(),
            platform: chatbot.platform,
            ai_provider: chatbot.ai_provider,
            client_id: chatbot.client_id,
            embed_code: chatbot.embed_code,
            notes: chatbot.notes
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this chatbot?')) {
            try {
                const response = await fetch(`${config.apiBaseUrl}/chatbots/delete.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ id }),
                });

                if (response.ok) {
                    fetchChatbots();
                }
            } catch (error) {
                console.error('Error deleting chatbot:', error);
            }
        }
    };

    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-center items-center h-64">
                    <div className="text-gray-600">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-center items-center h-64">
                    <div className="text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Chatbots</h1>
                <button
                    onClick={() => {
                        setEditingChatbot(null);
                        setFormData({
                            lead_id: '',
                            website_id: '',
                            platform: '',
                            ai_provider: '',
                            client_id: '',
                            embed_code: '',
                            notes: ''
                        });
                        setIsModalOpen(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                    Add Chatbot
                </button>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                {chatbots.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        No chatbots found. Click "Add Chatbot" to create one.
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {chatbots.map((chatbot) => (
                            <li key={chatbot.id}>
                                <div className="px-4 py-4 sm:px-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-primary-600 truncate">
                                                {chatbot.platform} - {chatbot.ai_provider}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                Client: {chatbot.lead_name} | Website: {chatbot.website_title}
                                            </p>
                                        </div>
                                        <div className="ml-4 flex-shrink-0 flex space-x-2">
                                            <button
                                                onClick={() => handleEdit(chatbot)}
                                                className="text-primary-600 hover:text-primary-900"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(chatbot.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed z-10 inset-0 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                            <form onSubmit={handleSubmit}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Client</label>
                                        <select
                                            value={formData.lead_id}
                                            onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                                            className="mt-1 block w-full border border-gray-400 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            required
                                            style={selectStyles}
                                        >
                                            <option value="">Select a client</option>
                                            {leads.map((lead) => (
                                                <option 
                                                    key={lead.id} 
                                                    value={lead.id}
                                                    title={lead.name}
                                                >
                                                    {lead.name.length > 50 ? lead.name.substring(0, 50) + '...' : lead.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Website</label>
                                        <select
                                            value={formData.website_id}
                                            onChange={(e) => setFormData({ ...formData, website_id: e.target.value })}
                                            className="mt-1 block w-full border border-gray-400 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            required
                                            style={selectStyles}
                                        >
                                            <option value="">Select a website</option>
                                            {websites.map((website) => (
                                                <option 
                                                    key={website.id} 
                                                    value={website.id}
                                                    title={website.website_title}
                                                >
                                                    {website.website_title.length > 50 ? website.website_title.substring(0, 50) + '...' : website.website_title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Platform</label>
                                        <input
                                            type="text"
                                            value={formData.platform}
                                            onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                                            className="mt-1 block w-full border border-gray-400 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">AI Provider</label>
                                        <input
                                            type="text"
                                            value={formData.ai_provider}
                                            onChange={(e) => setFormData({ ...formData, ai_provider: e.target.value })}
                                            className="mt-1 block w-full border border-gray-400 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Client ID</label>
                                        <input
                                            type="text"
                                            value={formData.client_id}
                                            onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                            className="mt-1 block w-full border border-gray-400 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Embed Code</label>
                                        <textarea
                                            value={formData.embed_code}
                                            onChange={(e) => setFormData({ ...formData, embed_code: e.target.value })}
                                            rows={4}
                                            className="mt-1 block w-full border border-gray-400 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            rows={3}
                                            className="mt-1 block w-full border border-gray-400 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        {editingChatbot ? 'Update' : 'Create'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chatbots; 